import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CircleX, X } from "lucide-react";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export default function CancelGlobalModal({
  open,
  busy,
  onClose,
  onConfirm,
  studentName,
  session,
  cancellationDate,
  memo,
  futureSessionsCount,
}) {
  const backButton = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    backButton.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, busy, onClose]);

  if (!open) return null;

  const pendingInvoices = (session?.receivables ?? [])
    .filter((invoice) => invoice.status !== "Paid")
    .sort((a, b) => String(a.due_date ?? "").localeCompare(String(b.due_date ?? "")));

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 1000, backgroundColor: "rgba(15, 23, 42, 0.55)" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-global-title"
        className="flex w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        style={{ maxWidth: 640, maxHeight: "85vh" }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="rounded-lg bg-red-50 p-2 text-red-600"><CircleX size={20} /></span>
            <div>
              <h2 id="cancel-global-title" className="text-base font-semibold text-slate-900">Cancel Global session</h2>
              <p className="text-xs text-slate-500">{studentName}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="rounded-md p-1 text-slate-500 cursor-pointer hover:bg-slate-200 disabled:cursor-not-allowed">
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 overflow-y-auto px-6 py-6 text-sm text-slate-700">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="block text-xs text-slate-500">Cancellation date</span>
              <span>{cancellationDate}</span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="block text-xs text-slate-500">Pending current invoices</span>
              <span>{pendingInvoices.length}</span>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="block text-xs text-slate-500">Future sessions to cancel</span>
              <span>{futureSessionsCount}</span>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Invoices that will remain payable</h3>
            {pendingInvoices.length === 0 ? (
              <p className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500">No pending invoices in the current session.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 font-medium">Invoice</th>
                      <th className="px-3 py-2 font-medium">Type</th>
                      <th className="px-3 py-2 font-medium">Due date</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-t border-slate-100">
                        <td className="px-3 py-2">{invoice.invoice_number ?? "–"}</td>
                        <td className="px-3 py-2">{invoice.type_detail || invoice.type || "–"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{invoice.due_date || "–"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">{currency.format(Number(invoice.amount) || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs text-slate-600">Future sessions will be canceled. The student remains active until inactivated in Students.</p>
            {memo && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <span className="block text-xs font-semibold text-slate-700">Memo</span>
                <p className="mt-1 text-sm text-slate-700" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{memo}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <button ref={backButton} type="button" onClick={onClose} disabled={busy} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-100 disabled:cursor-not-allowed">
            Back
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="rounded-lg bg-red-600 px-4 py-2 text-sm text-white cursor-pointer hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60">
            {busy ? "Canceling..." : "Confirm cancellation"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
